/**
 * RISKS-HOME-CHART
 * Display count of risks by level
 */

import Card from "@/components/card"
import { Risk, RiskFamily } from "../model/risk.types"
import Plot from "react-plotly.js"
import { PLOTLY_HOVERLABEL } from "@/utils/plotly-hover.utils"
import { chain, meanBy } from "lodash"
import DashboardNumber from "@/components/dashboard-number"
import { faTableList, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import { useAppSelector } from "@/core/root-reducer"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import RiskTypeChip from "./RiskTypeChip"
import Link from "@/components/link"
import Tooltip from "@/components/tooltip"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

interface Props {
  risks: Risk[]
  highestFamily?: RiskFamily | null
  rpsOnly?: boolean
}

export function RisksHomeChart(props: Props) {
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()
  const organization = useAppSelector(organizationStore.selectors.active)
  const units = useAppSelector(unitStore.selectors.list)

  // 🧮 Regroupe et trie les risques par niveau (valueEffectiveLabel)
  // 1️⃣ filter → risques PHY ou RPS selon la prop rpsOnly
  // 2️⃣ groupBy → regroupe tous les risques ayant le même label
  // 3️⃣ map → calcule pour chaque groupe : la couleur, le nombre et la moyenne des valueEffective
  // 4️⃣ orderBy → trie les groupes du risque le plus élevé au plus faible selon avgValue
  // 5️⃣ value() → retourne le résultat final sous forme de tableau exploitable
  const sorted = chain(props.risks)
    .filter((risk) => (props.rpsOnly ? risk.rps : !risk.rps))
    .groupBy("valueEffectiveLabel")
    .map((group, label) => ({
      label,
      color: group[0]?.valueEffectiveColor || "#ccc",
      count: group.length,
      avgValue: meanBy(group, "valueEffective") // moyenne pour trier
    }))
    .orderBy("avgValue", "desc") // trie décroissant (Élevé → Faible)
    .value()

  // puis
  const labels = sorted.map((x) => x.label)
  const values = sorted.map((x) => x.count)
  const colors = sorted.map((x) => x.color)

  return sorted.length === 0 ? (
    <Card className="flex-1">
      <div className="flex items-center">
        <DashboardNumber
          color="grey"
          count={0}
          icon={faTableList}
          title={t(props.rpsOnly ? "Risques RPS identifiés" : "Risques physiques identifiés")}
          fontSize={28}
        />
      </div>
    </Card>
  ) : (
    <Card className="relative flex flex-1 overflow-hidden" isWithoutPadding>
      <div className="flex items-start py-[20px] px-[20px] pr-[100px] gap-3">
        <div className="shrink-0">
          <div style={{ fontSize: 12 }} className="mb-4 text-center">
            <b>{t(props.rpsOnly ? "Risques RPS identifiés" : "Risques physiques identifiés")}</b>
          </div>

          {/* Chart */}
          <div className="w-[150px] h-[150px]">
            <Plot
              data={[
                {
                  type: "pie",
                  values,
                  labels,
                  hole: 0.4,
                  sort: false,
                  textinfo: "none",
                  hoverinfo: "label+value+percent",
                  marker: {
                    colors,
                    line: { color: "white", width: 2 }
                  },
                  hoverlabel: PLOTLY_HOVERLABEL as any
                }
              ]}
              layout={{
                hoverlabel: PLOTLY_HOVERLABEL,
                margin: { t: 0, b: 0, l: 0, r: 0 },
                showlegend: false,
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent"
              }}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 flex-1 min-w-0 overflow-hidden">
          <div style={{ fontSize: 12 }} className="flex items-center gap-2 mt-1">
            <span className="grey-t">Risque {sorted[0].label}</span>
            <RiskTypeChip
              isRps={props.rpsOnly || false}
              count={sorted[0].count}
              showLabel={false}
              disableTooltip
              color={sorted[0].color}
            />
          </div>

          {/* Risks list grouped by attribute */}
          <div className="flex flex-col gap-2">
            {(() => {
              const riskAttributeKey = organization?.riskAttributes?.[1]
              const risksAtLevel = props.risks.filter(
                (r) => r.valueEffectiveLabel === sorted[0].label && (props.rpsOnly ? r.rps : !r.rps)
              )

              // Group by attribute text
              const riskMap = new Map<string, { risk: (typeof risksAtLevel)[0]; units: string[] }>()
              risksAtLevel.forEach((risk) => {
                const riskText = riskAttributeKey ? risk.attributes?.[riskAttributeKey] : risk.name
                if (riskText) {
                  const existing = riskMap.get(riskText)
                  const unit = units.find((u) => u.id === risk.UnitId)
                  const unitName = unit?.name || "—"
                  if (existing) {
                    if (!existing.units.includes(unitName)) {
                      existing.units.push(unitName)
                    }
                  } else {
                    riskMap.set(riskText, { risk, units: [unitName] })
                  }
                }
              })

              const groupedRisks = Array.from(riskMap.values())
              const displayedRisks = isExpanded ? groupedRisks : groupedRisks.slice(0, 5)
              const hiddenCount = !isExpanded ? Math.max(0, groupedRisks.length - 5) : 0

              return (
                <>
                  {displayedRisks.map(({ risk, units: riskUnits }, idx) => {
                    const riskText = riskAttributeKey ? risk.attributes?.[riskAttributeKey] : risk.name
                    const associatedUnits =
                      risk.associatedRisks?.map((ar) => units.find((u) => u.id === ar.UnitId)?.name).filter(Boolean) ||
                      []
                    const allUnits = [...riskUnits, ...associatedUnits]
                    const tooltipText = allUnits.join(" | ")
                    const isLast = idx === displayedRisks.length - 1
                    return (
                      <div key={`${riskText}-${risk.id}`} style={{ fontSize: 11 }} className="min-w-0">
                        <Tooltip text={tooltipText} position={isLast ? "top" : "bottom"} width={200} usePortal>
                          <Link
                            isUnderline={true}
                            isWithoutMargin={true}
                            className="hover:font-semibold truncate block"
                            onClick={() => navigate(`/risks/risk/${risk.id}`, { state: { backTo: "/risks" } })}>
                            {riskText.length > 31 ? riskText.slice(0, 31) + "…" : riskText}
                            {allUnits.length > 1 && <span className="text-gray-500 ml-1">({allUnits.length}UT)</span>}
                          </Link>
                        </Tooltip>
                      </div>
                    )
                  })}
                  {groupedRisks.length > 5 && (
                    <Link
                      className="text-xs mt-1 text-gray-600 hover:text-gray-900"
                      onClick={() => setIsExpanded(!isExpanded)}
                      isWithoutMargin>
                      <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="text-xs mr-1" />
                      {isExpanded
                        ? t("Afficher moins")
                        : `${t("Voir")} ${hiddenCount} ${t("autre")}${hiddenCount > 1 ? "s" : ""}`}
                    </Link>
                  )}
                </>
              )
            })()}
          </div>
        </div>

      </div>

      {/* 🦉 Owl absolu, dans les bounds */}
      <img
        src={props.rpsOnly ? require("@/assets/owl-heart.png") : require("@/assets/owl-helmet.png")}
        alt="owl-thinking"
        className="absolute bottom-0 right-0 w-[95px] object-contain"
      />
    </Card>
  )
}
